const currentYear = new Date().getFullYear();

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-6 pb-2 border-b border-[var(--text-primary)]/10">
    {children}
  </h2>
);

const SubSection = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div className="mb-8">
    <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-3">
      {title}
    </h3>
    {children}
  </div>
);

const P = ({ children }: { children: React.ReactNode }) => (
  <p className="text-[var(--text-secondary)] leading-relaxed mb-3 text-[15px]">
    {children}
  </p>
);

const UL = ({ children }: { children: React.ReactNode }) => (
  <ul className="list-disc pl-5 text-[var(--text-secondary)] leading-relaxed space-y-2 text-[15px] mb-3">
    {children}
  </ul>
);

const Link = ({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) => (
  <a
    href={href}
    className="text-[var(--accent)] underline underline-offset-2"
    target="_blank"
    rel="noopener noreferrer"
  >
    {children}
  </a>
);

const EMAIL = "support@purelynovels.com";
const COMPANY = "PurelyNovels";
const ENTITY = "1000914058 ONTARIO INC.";

export default function LegalContent() {
  return (
    <div className="space-y-10">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-[var(--text-primary)] mb-2">
          Terms of Service & Privacy Policy
        </h1>
        <p className="text-[var(--text-secondary)] text-[15px]">
          Please read these terms and privacy policy carefully before using{" "}
          {COMPANY}'s services.
        </p>
      </div>

      {/* Terms of Service */}
      <section>
        <SectionTitle>Terms of Service</SectionTitle>

        <SubSection title="Acceptance of Terms">
          <P>
            By accessing, downloading, installing, or using {COMPANY} through
            our web application, iOS mobile application, or Android mobile
            application (collectively, the "Application"), you agree to be bound
            by these Terms of Service ("Terms"). If you do not agree to these
            Terms, do not use the Application.
          </P>
        </SubSection>

        <SubSection title="Description of Service">
          <P>
            {COMPANY} provides a digital reading platform through our web
            application, iOS mobile application, and Android mobile application
            (collectively, the "Application"). Our service allows you to browse,
            read, and enjoy novels and written content. The service uses
            artificial intelligence and machine learning technologies to enhance
            your reading experience.
          </P>
        </SubSection>

        <SubSection title="User Responsibilities">
          <P>
            You agree to use the Application only for lawful purposes and in
            accordance with these Terms. You are responsible for:
          </P>
          <UL>
            <li>
              Ensuring you use the Application in accordance with all applicable
              laws
            </li>
            <li>
              Not uploading or distributing content that violates any laws or
              third-party rights
            </li>
            <li>Maintaining the security of your account and device</li>
            <li>
              Not attempting to reverse engineer or modify the Application
            </li>
          </UL>
        </SubSection>

        <SubSection title="Content and Intellectual Property">
          <P>
            All content available through the Application, including but not
            limited to novels, text, images, and other materials, is protected
            by copyright and other intellectual property laws. You may not
            reproduce, distribute, modify, or create derivative works from any
            content without proper authorization.
          </P>
          <P>
            Your reading history, bookmarks, and preferences are stored in your
            account. You retain full control over your data and may delete it at
            any time through the Application.
          </P>
        </SubSection>

        <SubSection title="AI-Generated Content">
          <P>
            The stories available in this Application are generated and enhanced
            using artificial intelligence (AI) technology. While AI assists in
            creating and adapting content, all stories are reviewed and curated
            by our team. AI-generated content may occasionally contain errors or
            inconsistencies.
          </P>
        </SubSection>

        <SubSection title="Limitation of Liability">
          <P>
            The Application is provided "as is" without warranties of any kind.
            We are not liable for any damages arising from your use of the
            Application, including but not limited to content availability or
            data loss.
          </P>
        </SubSection>

        <SubSection title="Indemnification">
          <P>
            You agree to defend, indemnify, and hold harmless {COMPANY},{" "}
            {ENTITY}, and their respective affiliates, officers, directors,
            employees, contractors, agents, licensors, and suppliers from and
            against any and all claims, liabilities, damages, judgments, awards,
            losses, costs, expenses, or fees (including reasonable attorneys'
            fees) arising out of or relating to:
          </P>
          <UL>
            <li>
              Your violation of these Terms of Service or any applicable law or
              regulation
            </li>
            <li>Your use or misuse of the Application</li>
            <li>
              Your violation of any rights of another person or entity,
              including any intellectual property rights or privacy rights
            </li>
            <li>Any content you upload or create using the Application</li>
          </UL>
        </SubSection>

        <SubSection title="Time Limit for Claims">
          <P>
            You agree that regardless of any statute or law to the contrary, any
            claim or cause of action arising out of or related to your use of
            the Application or these Terms must be filed within one (1) year
            after such claim or cause of action arose or be forever barred.
          </P>
        </SubSection>

        <SubSection title="Termination">
          <P>
            We may terminate or suspend your access to the Application at any
            time, with or without cause, and with or without notice, if you fail
            to comply with these Terms of Service or for any other reason at our
            sole discretion.
          </P>
          <P>
            You may terminate your account at any time by contacting us at{" "}
            <Link href={`mailto:${EMAIL}`}>{EMAIL}</Link>. Any fees paid prior
            to termination are non-refundable.
          </P>
          <P>
            Upon termination, all licenses and rights granted to you under these
            Terms will immediately cease, but all provisions which by their
            nature should survive termination shall survive, including without
            limitation, indemnification obligations, warranty disclaimers, and
            limitations of liability.
          </P>
        </SubSection>

        <SubSection title="Subscription and Refund Policy">
          <P>
            If you subscribe to any paid features of the Application, your
            subscription will automatically renew at the end of each billing
            period unless you cancel it before the renewal date. It is your sole
            responsibility to cancel your subscription at least 24 hours before
            the renewal date to avoid being charged for the next billing period.
          </P>
          <P>
            <strong>No Refunds:</strong> All subscription fees are
            non-refundable except in cases where there is a technical issue with
            the Application that prevents you from using the service as
            intended. Refunds will only be considered if you report such issues
            to us at <Link href={`mailto:${EMAIL}`}>{EMAIL}</Link> and we are
            unable to resolve the problem within a reasonable timeframe.
          </P>
          <P>
            By subscribing to the Application, you acknowledge and agree to this
            refund policy and understand that you will not receive a refund for
            any unused portion of your subscription period, even if you cancel
            your subscription before the end of the billing period.
          </P>
        </SubSection>

        <SubSection title="Class Action Waiver">
          <P>
            <strong>
              YOU AND {COMPANY.toUpperCase()} AGREE THAT EACH MAY BRING CLAIMS
              AGAINST THE OTHER ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY AND NOT
              AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS OR
              REPRESENTATIVE PROCEEDING.
            </strong>
          </P>
          <P>
            Unless both you and we agree otherwise, any arbitrator or court may
            not consolidate more than one person's claims and may not otherwise
            preside over any form of a representative or class proceeding.
          </P>
        </SubSection>

        <SubSection title="Governing Law and Jurisdiction">
          <P>
            These Terms of Service and any disputes arising out of or related to
            the Application or these Terms shall be governed by and construed in
            accordance with the laws of the Province of Ontario and the federal
            laws of Canada applicable therein, without regard to conflict of law
            principles.
          </P>
          <P>
            Subject to the arbitration provisions below, you agree that any
            legal action or proceeding between you and {COMPANY} for any purpose
            concerning these Terms or the parties' obligations hereunder shall
            be brought exclusively in the courts located in Toronto, Ontario,
            Canada, and you hereby irrevocably consent to the personal
            jurisdiction and venue of such courts.
          </P>
        </SubSection>

        <SubSection title="Dispute Resolution and Arbitration">
          <P>
            <strong>Please Read This Section Carefully.</strong> It affects your
            legal rights, including your right to file a lawsuit in court.
          </P>
          <P>
            Most disputes can be resolved informally. Before filing a claim, you
            agree to try to resolve the dispute by contacting us at{" "}
            <Link href={`mailto:${EMAIL}`}>{EMAIL}</Link>. We'll try to resolve
            the dispute informally by contacting you via email. If a dispute is
            not resolved within 30 days of submission, you or we may bring a
            formal proceeding.
          </P>
          <P>
            Either party may elect to pursue a claim in small claims court in
            Ontario, Canada, rather than through arbitration, so long as the
            matter remains in small claims court and proceeds only on an
            individual (non-class, non-representative) basis.
          </P>
        </SubSection>

        <SubSection title="Severability and Waiver">
          <P>
            If any provision of these Terms is found to be invalid,
            unenforceable, or illegal by a court of competent jurisdiction, the
            remaining provisions will remain in full force and effect.
          </P>
          <P>
            Our failure to enforce any right or provision of these Terms will
            not be considered a waiver of those rights.
          </P>
        </SubSection>

        <SubSection title="Changes to Terms of Service">
          <P>
            We reserve the right to modify these Terms of Service at any time.
            We will provide notice of material changes by posting the updated
            Terms on the Application. Your continued use of the Application
            after the effective date of any changes constitutes your acceptance
            of the revised Terms.
          </P>
        </SubSection>
      </section>

      {/* Privacy Policy */}
      <section>
        <SectionTitle>Privacy Policy</SectionTitle>

        <SubSection title="Introduction">
          <P>
            <strong>Effective Date:</strong> {currentYear}
          </P>
          <P>
            {ENTITY} ("Company," "our," "we") respects your privacy and is
            committed to protecting it through our compliance with this Privacy
            Policy. This Privacy Policy is part of our Terms of Use.
          </P>
          <P>
            In this policy we describe the types of information we may collect
            from you or that you may provide when you use our web application,
            iOS mobile application, or Android mobile application (collectively,
            the "Application") and our practices for collecting, using,
            protecting, and disclosing that information.
          </P>
        </SubSection>

        <SubSection title="Children Under the Age of 13">
          <P>
            The Application is not intended for children under 13 years of age
            and you are not allowed to use the Application or provide
            information on it if you are under 13 years of age. If we learn we
            have received personal information from or about a child under 13,
            we will delete that information immediately. If you believe we might
            have any information from or about a child under 13, please contact
            us immediately at <Link href={`mailto:${EMAIL}`}>{EMAIL}</Link>.
          </P>
        </SubSection>

        <SubSection title="Information We Collect About You">
          <P>We collect the following types of information:</P>
          <UL>
            <li>
              <strong>Usage Information:</strong> Information about your
              internet connection, the equipment you use to access the
              Application, and Application usage details to help diagnose
              potential issues.
            </li>
            <li>
              <strong>Reading Data:</strong> Your reading history, bookmarks,
              preferences, and progress within the Application.
            </li>
            <li>
              <strong>Account Information:</strong> Information you provide when
              creating an account, such as your email address.
            </li>
          </UL>
        </SubSection>

        <SubSection title="How We Collect Information From You">
          <P>
            We collect generic information about your usage and equipment
            automatically as you navigate through the Application, such as usage
            details, app release, app version, device model, and time zone.
            Information that is automatically collected from you is used
            primarily for analytics purposes to improve the Application.
          </P>
        </SubSection>

        <SubSection title="How We Use Your Information">
          <P>
            We use information that we collect about you or that you provide to
            us for the following purposes:
          </P>
          <UL>
            <li>
              To provide reading services and personalize your experience.
            </li>
            <li>
              To store and manage your reading progress, bookmarks, and
              preferences within your account.
            </li>
            <li>
              To develop, maintain, and improve the Application and its
              services.
            </li>
            <li>
              To analyze Application performance, user behavior, and diagnose
              technical issues.
            </li>
          </UL>
        </SubSection>

        <SubSection title="Disclosure of Your Information">
          <P>
            We may disclose aggregated information about our users, and
            information that does not identify any individual, without
            restriction.
          </P>
          <P>
            We may disclose personal information that we collect or you provide:
          </P>
          <UL>
            <li>To our subsidiaries and affiliates.</li>
            <li>
              To contractors, service providers, and other third parties we use
              to support our business, including Amazon Web Services (AWS),
              Cloudflare, and Firebase. These service providers are bound by
              contractual obligations to keep personal information confidential.
            </li>
            <li>
              To a buyer or other successor in the event of a merger,
              divestiture, restructuring, reorganization, dissolution, or other
              sale or transfer of some or all of the Company's assets.
            </li>
            <li>
              To comply with any court order, law, or legal process, including
              to respond to any government or regulatory request.
            </li>
            <li>
              If we believe disclosure is necessary or appropriate to protect
              the rights, property, or safety of the Company, our customers, or
              others.
            </li>
          </UL>
        </SubSection>

        <SubSection title="Your Data Protection Rights">
          <P>
            Depending on your location, you may have certain rights regarding
            your personal information. We are committed to honoring these rights
            and will respond to all legitimate requests within the timeframes
            required by applicable law.
          </P>
          <P>
            To exercise any of these rights, please contact us at{" "}
            <Link href={`mailto:${EMAIL}`}>{EMAIL}</Link> with the subject line
            "Data Rights Request."
          </P>
        </SubSection>

        <SubSection title="Data Security">
          <P>
            We implement industry-standard security measures to protect your
            information:
          </P>
          <UL>
            <li>
              All data transmitted to and from the Application is encrypted
              using SSL technology.
            </li>
            <li>
              Your data is stored on secure servers with encryption at rest.
            </li>
            <li>
              Access to your data is restricted to authorized personnel and
              systems only.
            </li>
          </UL>
          <P>
            The safety and security of your information also depends on you. Do
            not share your passwords with anyone and be careful about giving out
            information in public areas of the Application.
          </P>
        </SubSection>

        <SubSection title="Changes to Our Privacy Policy">
          <P>
            We may update this Privacy Policy from time to time. We will post
            any changes we make to our privacy policy on this page. Your
            continued use of the Application after we make changes constitutes
            your acceptance of those changes.
          </P>
        </SubSection>
      </section>

      {/* Contact */}
      <section className="mb-8">
        <SubSection title="Contact Information">
          <P>
            To ask questions or comment about these Terms of Service or Privacy
            Policy, contact us at <Link href={`mailto:${EMAIL}`}>{EMAIL}</Link>.
          </P>
        </SubSection>
      </section>

      <footer className="pt-6 border-t border-[var(--text-primary)]/10">
        <p className="text-sm text-[var(--text-muted)] text-center">
          Copyright {currentYear} {COMPANY}. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
